import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  LogOut, CalendarDays, ClipboardList, BarChart3,
  Check, X as XIcon, Trash2, Ban, Plus, ArrowLeft,
  Euro, RotateCcw, Link2, Copy, Image as ImageIcon,
  Home, AlertTriangle, Mail, Settings, DollarSign,
  Users, TrendingUp, Clock, Plane, PlaneLanding, Star, ListChecks,
  ChefHat, RefreshCcw, Shield, Package, Download, Filter, ScrollText, Music,
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';

import ReservationDetail from '@/components/ReservationDetail';
import TasksOverview from '@/components/TasksOverview';
import AdminPlanningTimeline from '@/components/AdminPlanningTimeline';
import { IcalHealthWidget } from '@/components/IcalHealthWidget';
import AdminImportReservations from '@/components/AdminImportReservations';
import AdminNewReservation from '@/components/AdminNewReservation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FORMULES = {
  'appartement-1': 'Apt Terrasse',
  'appartement-2': 'Apt Panorama',
  'gite-seul': 'Gite seul',
  'gite-salle': 'Gite + Salle',
  'salle-seule': 'Salle seule',
  // Legacy
  'domaine-complet': 'Domaine + Salle',
  'gite-complet': 'Gite complet',
  'appartement-individuel': 'Appartement',
};

const STATUS_MAP = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  confirmed: { label: 'Confirmee', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  cancelled: { label: 'Annulee', color: 'bg-red-100 text-red-800 border-red-200' },
  cancelled_overdue: { label: 'Annulee (impaye)', color: 'bg-red-100 text-red-800 border-red-200' },
  imported: { label: 'Importe', color: 'bg-blue-100 text-blue-800 border-blue-200' },
};

const SOURCE_COLORS = {
  'Site direct': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Airbnb': 'bg-rose-50 text-rose-700 border-rose-200',
  'Booking': 'bg-blue-50 text-blue-700 border-blue-200',
  'Abritel': 'bg-violet-50 text-violet-700 border-violet-200',
  'Externe': 'bg-gray-100 text-gray-600 border-gray-200',
};

const EMAIL_TYPES_LABELS = {
  j_minus_30: 'Preparez votre sejour (J-30)',
  j_minus_7: 'Plus que 7 jours ! (J-7)',
  j_plus_1: 'Merci et a bientot (J+1)',
  j_plus_90: 'On revient ? (3 mois)',
};

function useAdmin() {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');

  const api = useCallback((method, url, data) => {
    return axios({
      method, url: `${BACKEND_URL}${url}`, data,
      headers: { Authorization: `Bearer ${token}` },
    });
  }, [token]);

  const logout = useCallback(() => {
    const rt = localStorage.getItem('admin_refresh_token');
    axios.post(`${BACKEND_URL}/api/admin/logout`, { refresh_token: rt || null }).catch(() => {});
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_refresh_token');
    localStorage.removeItem('admin_email');
    navigate('/admin');
  }, [navigate]);

  useEffect(() => {
    if (!token) navigate('/admin');
  }, [token, navigate]);

  return { api, logout, token };
}

export default function AdminDashboard() {
  const { api, logout, token } = useAdmin();
  const navigate = useNavigate();
  const [tab, setTab] = useState('accueil');
  const [reservations, setReservations] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  // Dashboard overview
  const [overview, setOverview] = useState(null);
  const [adminName, setAdminName] = useState('');

  // Block dates dialog
  const [blockDialog, setBlockDialog] = useState(false);
  const [occupantsModal, setOccupantsModal] = useState(null);
  const [occAdults, setOccAdults] = useState('');
  const [occChildren, setOccChildren] = useState('0');
  const [occSaving, setOccSaving] = useState(false);
  const [taxSummary, setTaxSummary] = useState(null);
  const [blockFormula, setBlockFormula] = useState('appartement-1');
  const [blockNoEnd, setBlockNoEnd] = useState(false);
  const [blockStart, setBlockStart] = useState(null);
  const [blockEnd, setBlockEnd] = useState(null);
  const [blockStartOpen, setBlockStartOpen] = useState(false);
  const [blockEndOpen, setBlockEndOpen] = useState(false);
  const [blockReason, setBlockReason] = useState('');

  // Delete confirm
  const [deleteId, setDeleteId] = useState(null);

  // Availability filters
  const [availFilterFormula, setAvailFilterFormula] = useState('all');
  const [availFilterOrigin, setAvailFilterOrigin] = useState('all');

  // Display-layer dedup of iCal mirror events.
  // Airbnb reflects each booking across multiple listings (e.g. a single
  // "whole estate" event creates separate iCal entries on Apt1, Apt2 and Gite).
  // We collapse these mirrored entries using the same business rules as the
  // planning timeline so the Reservations list shows ONE row per real event.
  const CROSS_BLOCKS_FRONT = {
    'appartement-1': ['appartement-1', 'gite-seul', 'gite-salle', 'salle-seule'],
    'appartement-2': ['appartement-2', 'gite-seul', 'gite-salle', 'salle-seule'],
    'gite-seul': ['gite-seul', 'gite-salle', 'salle-seule', 'appartement-1', 'appartement-2'],
    'gite-salle': ['gite-salle', 'gite-seul', 'salle-seule', 'appartement-1', 'appartement-2'],
    'salle-seule': ['salle-seule', 'gite-salle', 'gite-seul', 'appartement-1', 'appartement-2'],
  }; // kept for reference / potential future use
  void CROSS_BLOCKS_FRONT;

  const displayReservations = useMemo(() => {
    const isIcal = (r) => r && r.entry_type === 'ical_import';
    const direct = reservations.filter(r => !isIcal(r));
    // Closures ('Indisponibilite Airbnb/Booking') are not bookings → keep them off the reservations list
    const ical = reservations.filter(r => isIcal(r) && r.ical_kind !== 'closure');

    // 1) Drop iCal entries already covered by a direct/Amenitiz reservation on the same acc & overlap
    const intervalsOverlap = (a, b) => a.arrival_date < b.departure_date && b.arrival_date < a.departure_date;
    const icalKept = ical.filter(ic => !direct.some(d =>
      d.accommodation_type === ic.accommodation_type && intervalsOverlap(d, ic)
    ));

    // 2) Group remaining iCal entries by (arrival, departure, platform) and apply Airbnb mirror rules
    const groups = {};
    for (const ic of icalKept) {
      const k = `${ic.source || 'Externe'}__${ic.arrival_date}__${ic.departure_date}`;
      (groups[k] = groups[k] || []).push(ic);
    }
    const FORMULES_SHORT = { 'appartement-1': 'Apt Terrasse', 'appartement-2': 'Apt Panorama', 'gite-seul': 'Gite seul', 'gite-salle': 'Gite + Salle' };
    const icalDeduped = [];
    for (const group of Object.values(groups)) {
      const accs = new Set(group.map(g => g.accommodation_type));
      const hasA1 = accs.has('appartement-1');
      const hasA2 = accs.has('appartement-2');
      const hasG = accs.has('gite-seul');
      let keep;
      if (hasA1 && hasA2 && hasG) keep = new Set(['gite-seul']);          // whole estate -> Gite
      else if (hasA1 && hasG && !hasA2) keep = new Set(['appartement-1']); // Apt1 reflected on Gite
      else if (hasA2 && hasG && !hasA1) keep = new Set(['appartement-2']); // Apt2 reflected on Gite
      else keep = accs;                                                    // 2 independent apartments
      for (const ic of group) if (keep.has(ic.accommodation_type)) icalDeduped.push(ic);
    }
    void FORMULES_SHORT;
    return [...direct, ...icalDeduped].sort((a, b) => (b.arrival_date || '').localeCompare(a.arrival_date || ''));
  }, [reservations]);

  // Refund dialog
  const [refundId, setRefundId] = useState(null);
  const [refundType, setRefundType] = useState('deposit');
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundResult, setRefundResult] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);

  // Overdue management
  const [overdueReport, setOverdueReport] = useState(null);
  const [autoCancelLoading, setAutoCancelLoading] = useState(false);
  const [autoCancelResult, setAutoCancelResult] = useState(null);

  // Email management
  const [emailStats, setEmailStats] = useState(null);
  const [emails, setEmails] = useState([]);
  const [emailPreview, setEmailPreview] = useState(null);
  const [triggerResult, setTriggerResult] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateSubject, setTemplateSubject] = useState('');
  const [templateBody, setTemplateBody] = useState('');
  const [templates, setTemplates] = useState({});
  const [templateSaveMsg, setTemplateSaveMsg] = useState('');

  // Reservation detail
  const [selectedReservation, setSelectedReservation] = useState(null);

  // Reservation filters
  const [filterSource, setFilterSource] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [showNewResa, setShowNewResa] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [resRes, availRes, statsRes] = await Promise.all([
        api('get', '/api/admin/reservations-unified'),
        api('get', '/api/availability'),
        api('get', '/api/admin/stats'),
      ]);
      setReservations(resRes.data);
      setBlockedDates(availRes.data);
      setStats(statsRes.data);
      api('get', '/api/admin/tourist-tax/externes').then(r => setTaxSummary(r.data)).catch(() => {});
    } catch {
      logout();
    } finally {
      setLoading(false);
    }
  }, [api, logout]);

  const fetchOverview = useCallback(async () => {
    try {
      const [ovRes, setRes] = await Promise.all([
        api('get', '/api/admin/dashboard-overview'),
        api('get', '/api/admin/settings'),
      ]);
      setOverview(ovRes.data);
      setAdminName(setRes.data?.admin_name || '');
    } catch { /* ignore */ }
  }, [api]);

  const fetchOverdueReport = useCallback(async () => {
    try {
      const res = await api('get', '/api/admin/payments/overdue-report');
      setOverdueReport(res.data);
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => { fetchData(); fetchOverview(); }, [fetchData, fetchOverview]);
  useEffect(() => { if (tab === 'paiements') fetchOverdueReport(); }, [tab, fetchOverdueReport]);

  const fetchEmailStats = useCallback(async () => {
    try {
      const [statsRes, listRes, tplRes] = await Promise.all([
        api('get', '/api/admin/emails/stats'),
        api('get', '/api/admin/emails'),
        api('get', '/api/admin/emails/templates'),
      ]);
      setEmailStats(statsRes.data);
      setEmails(listRes.data);
      setTemplates(tplRes.data);
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => { if (tab === 'emails') fetchEmailStats(); }, [tab, fetchEmailStats]);

  const toggleEmailType = async (etype) => {
    if (!emailStats) return;
    const disabled = emailStats.disabled_types || [];
    const newDisabled = disabled.includes(etype) ? disabled.filter(t => t !== etype) : [...disabled, etype];
    try {
      await api('put', '/api/admin/emails/settings', { disabled_types: newDisabled });
      fetchEmailStats();
    } catch { /* ignore */ }
  };

  const toggleEmailPause = async () => {
    if (!emailStats) return;
    try {
      await api('put', '/api/admin/emails/settings', { paused: !emailStats.paused });
      fetchEmailStats();
    } catch { /* ignore */ }
  };

  const triggerEmails = async () => {
    try {
      const res = await api('post', '/api/admin/emails/trigger');
      setTriggerResult(res.data);
      fetchEmailStats();
      setTimeout(() => setTriggerResult(null), 3000);
    } catch { /* ignore */ }
  };

  const previewEmail = async (etype) => {
    try {
      const res = await api('get', `/api/admin/emails/preview/${etype}`);
      setEmailPreview(res.data);
    } catch { /* ignore */ }
  };

  const openEditTemplate = (etype) => {
    const tpl = templates[etype];
    if (tpl) {
      setTemplateSubject(tpl.subject);
      setTemplateBody(tpl.body_text);
    }
    setEditingTemplate(etype);
    setTemplateSaveMsg('');
  };

  const saveTemplate = async () => {
    if (!editingTemplate) return;
    try {
      await api('put', `/api/admin/emails/templates/${editingTemplate}`, {
        subject: templateSubject,
        body_text: templateBody,
      });
      setTemplateSaveMsg('Template sauvegarde !');
      fetchEmailStats();
      setTimeout(() => setTemplateSaveMsg(''), 2000);
    } catch { /* ignore */ }
  };

  const resetTemplate = async () => {
    if (!editingTemplate) return;
    try {
      await api('delete', `/api/admin/emails/templates/${editingTemplate}`);
      const tplRes = await api('get', '/api/admin/emails/templates');
      setTemplates(tplRes.data);
      const tpl = tplRes.data[editingTemplate];
      if (tpl) {
        setTemplateSubject(tpl.subject);
        setTemplateBody(tpl.body_text);
      }
      setTemplateSaveMsg('Template reinitialise !');
      setTimeout(() => setTemplateSaveMsg(''), 2000);
    } catch { /* ignore */ }
  };

  const handleAutoCancel = async () => {
    setAutoCancelLoading(true);
    try {
      const res = await api('post', '/api/admin/payments/check-overdue');
      setAutoCancelResult(res.data);
      fetchData();
      fetchOverdueReport();
    } catch (e) {
      setAutoCancelResult({ message: e.response?.data?.detail || 'Erreur' });
    } finally {
      setAutoCancelLoading(false);
    }
  };

  const handleReleaseCaution = async (reservationId) => {
    try {
      await api('post', `/api/admin/cautions/${reservationId}/release`);
      fetchData();
    } catch { /* ignore */ }
  };

  const handleAuthorizeCaution = async (reservationId) => {
    try {
      await api('post', `/api/admin/cautions/${reservationId}/authorize`);
      fetchData();
    } catch { /* ignore */ }
  };

  const updateStatus = async (id, status) => {
    await api('put', `/api/admin/reservations/${id}/status`, { status });
    fetchData();
  };

  const saveOccupants = async () => {
    if (!occupantsModal || !occAdults || parseInt(occAdults) < 1) {
      toast.error('Le nombre d\'adultes est obligatoire (minimum 1)');
      return;
    }
    setOccSaving(true);
    try {
      const res = await api('put', `/api/admin/reservations/${occupantsModal.id}/occupants`, {
        adults: parseInt(occAdults),
        children: parseInt(occChildren) || 0,
      });
      toast.success(`Occupants enregistres — taxe de sejour recalculee : ${res.data.new_tourist_tax.toFixed(2)} €`);
      setOccupantsModal(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la mise a jour');
    }
    setOccSaving(false);
  };

  const cancelWithUndo = (id) => {
    let undone = false;
    const timer = setTimeout(async () => {
      if (undone) return;
      await updateStatus(id, 'cancelled');
      toast.success('Reservation annulee');
    }, 5000);
    toast.warning('Annulation de la reservation dans 5 secondes...', {
      duration: 5000,
      action: {
        label: 'Annuler',
        onClick: () => { undone = true; clearTimeout(timer); toast.info("Action annulee — la reservation n'a pas ete modifiee"); },
      },
    });
  };

  const deleteReservation = async (id) => {
    await api('delete', `/api/admin/reservations/${id}`);
    setDeleteId(null);
    fetchData();
  };

  const blockDates = async () => {
    if (!blockStart || (!blockEnd && !blockNoEnd)) return;
    await api('post', '/api/admin/availability/block', {
      accommodation_type: blockFormula,
      start_date: format(blockStart, 'yyyy-MM-dd'),
      end_date: blockNoEnd ? '2099-12-31' : format(blockEnd, 'yyyy-MM-dd'),
      reason: blockReason || 'manual',
    });
    setBlockDialog(false);
    setBlockStart(null);
    setBlockEnd(null);
    setBlockNoEnd(false);
    setBlockReason('');
    fetchData();
  };

  const unblockDate = async (id) => {
    await api('delete', `/api/admin/availability/${id}`);
    fetchData();
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="bg-[#2C3E30] text-[#F2F0E9] px-6 md:px-12 py-4">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="text-[#F2F0E9]/70 hover:text-[#F2F0E9]" data-testid="admin-home-link">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <h1 className="font-['Playfair_Display'] text-lg md:text-xl">Administration</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="admin-medias-link"
              onClick={() => navigate('/admin/medias')}
              variant="ghost"
              className="text-[#C4A962] hover:text-[#C4A962] hover:bg-[#C4A962]/10"
              size="sm"
            >
              <ImageIcon className="w-4 h-4 mr-1" strokeWidth={1.5} /> Medias
            </Button>
            <Button
              data-testid="admin-comptabilite-link"
              onClick={() => navigate('/admin/comptabilite')}
              variant="ghost"
              className="text-[#C4A962] hover:text-[#C4A962] hover:bg-[#C4A962]/10"
              size="sm"
            >
              <BarChart3 className="w-4 h-4 mr-1" strokeWidth={1.5} /> Comptabilite
            </Button>
            <Button
              data-testid="admin-tarifs-link"
              onClick={() => navigate('/admin/tarifs')}
              variant="ghost"
              className="text-[#C4A962] hover:text-[#C4A962] hover:bg-[#C4A962]/10"
              size="sm"
            >
              <DollarSign className="w-4 h-4 mr-1" strokeWidth={1.5} /> Tarifs
            </Button>
            <Button
              data-testid="admin-avis-link"
              onClick={() => navigate('/admin/avis')}
              variant="ghost"
              className="text-[#C4A962] hover:text-[#C4A962] hover:bg-[#C4A962]/10"
              size="sm"
            >
              <Star className="w-4 h-4 mr-1" strokeWidth={1.5} /> Avis
            </Button>
            <Button
              data-testid="admin-traiteurs-link"
              onClick={() => navigate('/admin/traiteurs')}
              variant="ghost"
              className="text-[#C4A962] hover:text-[#C4A962] hover:bg-[#C4A962]/10"
              size="sm"
            >
              <ChefHat className="w-4 h-4 mr-1" strokeWidth={1.5} /> Traiteurs
            </Button>
            <Button
              data-testid="admin-djs-link"
              onClick={() => navigate('/admin/djs')}
              variant="ghost"
              className="text-[#C4A962] hover:text-[#C4A962] hover:bg-[#C4A962]/10"
              size="sm"
            >
              <Music className="w-4 h-4 mr-1" strokeWidth={1.5} /> DJ
            </Button>
            <Button
              data-testid="admin-cuisine-link"
              onClick={() => navigate('/admin/cuisine')}
              variant="ghost"
              className="text-[#C4A962] hover:text-[#C4A962] hover:bg-[#C4A962]/10"
              size="sm"
            >
              <ChefHat className="w-4 h-4 mr-1" strokeWidth={1.5} /> Cuisine
            </Button>
            <Button
              data-testid="admin-refunds-link"
              onClick={() => navigate('/admin/remboursements')}
              variant="ghost"
              className="text-[#C4A962] hover:text-[#C4A962] hover:bg-[#C4A962]/10"
              size="sm"
            >
              <RefreshCcw className="w-4 h-4 mr-1" strokeWidth={1.5} /> Remboursements
            </Button>
            <Button
              data-testid="admin-cautions-link"
              onClick={() => navigate('/admin/cautions')}
              variant="ghost"
              className="text-[#C4A962] hover:text-[#C4A962] hover:bg-[#C4A962]/10"
              size="sm"
            >
              <Shield className="w-4 h-4 mr-1" strokeWidth={1.5} /> Cautions
            </Button>
            <Button
              data-testid="admin-extras-link"
              onClick={() => navigate('/admin/extras')}
              variant="ghost"
              className="text-[#C4A962] hover:text-[#C4A962] hover:bg-[#C4A962]/10"
              size="sm"
            >
              <Package className="w-4 h-4 mr-1" strokeWidth={1.5} /> Extras
            </Button>
            <Button
              data-testid="admin-ical-link"
              onClick={() => navigate('/admin/ical')}
              variant="ghost"
              className="text-[#C4A962] hover:text-[#C4A962] hover:bg-[#C4A962]/10"
              size="sm"
            >
              <Link2 className="w-4 h-4 mr-1" strokeWidth={1.5} /> iCal
            </Button>
            <Button
              data-testid="admin-legal-link"
              onClick={() => navigate('/admin/pages-legales')}
              variant="ghost"
              className="text-[#C4A962] hover:text-[#C4A962] hover:bg-[#C4A962]/10"
              size="sm"
            >
              <ScrollText className="w-4 h-4 mr-1" strokeWidth={1.5} /> Pages legales
            </Button>
            <Button
              data-testid="admin-settings-link"
              onClick={() => navigate('/admin/parametres')}
              variant="ghost"
              className="text-[#C4A962] hover:text-[#C4A962] hover:bg-[#C4A962]/10"
              size="sm"
            >
              <Settings className="w-4 h-4 mr-1" strokeWidth={1.5} />
            </Button>
            <span className="text-sm text-[#F2F0E9]/70 hidden md:block">{localStorage.getItem('admin_email')}</span>
            <Button
              data-testid="admin-logout-btn"
              onClick={logout}
              variant="ghost"
              className="text-[#F2F0E9]/70 hover:text-[#F2F0E9] hover:bg-white/10"
              size="sm"
            >
              <LogOut className="w-4 h-4 mr-1" strokeWidth={1.5} /> Deconnexion
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        {/* Stats - only on detail tabs */}
        {tab !== 'accueil' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total', value: stats.total || 0, icon: ClipboardList },
            { label: 'En attente', value: stats.pending || 0, icon: CalendarDays },
            { label: 'Confirmees', value: stats.confirmed || 0, icon: Check },
            { label: 'Dates bloquees', value: stats.blocked_dates || 0, icon: Ban },
          ].map(s => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}
              className="bg-white rounded-xl p-5 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F5F5F0] flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-[#8A9A5B]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-[#2C3E30]">{s.value}</p>
                  <p className="text-xs text-[#595959]">{s.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {['accueil', 'planning', 'reservations', 'taches', 'paiements', 'emails', 'disponibilites', 'import'].map(t => (
            <Button
              key={t}
              data-testid={`admin-tab-${t}`}
              variant={tab === t ? 'default' : 'outline'}
              onClick={() => setTab(t)}
              className={`rounded-full px-5 text-sm ${
                tab === t ? 'bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040]' : 'border-[#E5E5E0] text-[#595959]'
              }`}
            >
              {t === 'accueil' ? 'Tableau de bord' : t === 'planning' ? 'Planning' : t === 'reservations' ? 'Reservations' : t === 'taches' ? 'Taches' : t === 'paiements' ? 'Paiements' : t === 'emails' ? 'Emails' : t === 'import' ? 'Import' : 'Disponibilites'}
            </Button>
          ))}
        </div>


        {/* Import tab */}
        {tab === 'import' && (
          <AdminImportReservations />
        )}

        {/* Planning timeline tab */}
        {tab === 'planning' && (
          <AdminPlanningTimeline onOpenReservation={(r) => setSelectedReservation(r)} />
        )}

        {/* Accueil / Tableau de bord tab */}
        {tab === 'accueil' && overview && (
          <div className="space-y-6">
            {/* Welcome */}
            <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
              <h2 data-testid="dashboard-welcome" className="font-['Playfair_Display'] text-2xl text-[#2C3E30]">
                Bienvenue{adminName ? `, ${adminName}` : ''}
              </h2>
              <p className="text-sm text-[#595959] mt-1">{new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>

            {/* Sante des synchronisations iCal */}
            <IcalHealthWidget />

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div data-testid="kpi-arrivals" className="bg-white rounded-xl p-5 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                    <PlaneLanding className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-[#2C3E30]">{overview.arrivals_today?.length || 0}</p>
                    <p className="text-xs text-[#595959]">Arrivees aujourd'hui</p>
                  </div>
                </div>
              </div>
              <div data-testid="kpi-departures" className="bg-white rounded-xl p-5 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                    <Plane className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-[#2C3E30]">{overview.departures_today?.length || 0}</p>
                    <p className="text-xs text-[#595959]">Departs aujourd'hui</p>
                  </div>
                </div>
              </div>
              <div data-testid="kpi-revenue" className="bg-white rounded-xl p-5 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#C4A962]/10 flex items-center justify-center">
                    <Euro className="w-5 h-5 text-[#C4A962]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-[#2C3E30]">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(overview.month_revenue)}</p>
                    <p className="text-xs text-[#595959]">CA du mois</p>
                  </div>
                </div>
                {/* Revenue gauge */}
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-[#595959] mb-1">
                    <span>Objectif : {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(overview.monthly_target)}</span>
                    <span>{Math.min(Math.round((overview.month_revenue / overview.monthly_target) * 100), 100)}%</span>
                  </div>
                  <div className="h-2 bg-[#E5E5E0] rounded-full overflow-hidden">
                    <div className="h-full bg-[#C4A962] rounded-full transition-all" style={{ width: `${Math.min((overview.month_revenue / overview.monthly_target) * 100, 100)}%` }} />
                  </div>
                </div>
              </div>
              <div data-testid="kpi-occupancy" className="bg-white rounded-xl p-5 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#8A9A5B]/10 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-[#8A9A5B]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-[#2C3E30]">{overview.occupancy_rate}%</p>
                    <p className="text-xs text-[#595959]">Taux d'occupation</p>
                  </div>
                </div>
                <div className="mt-3 h-2 bg-[#E5E5E0] rounded-full overflow-hidden">
                  <div className="h-full bg-[#8A9A5B] rounded-full transition-all" style={{ width: `${overview.occupancy_rate}%` }} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Alerts */}
              <div className="lg:col-span-2 bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
                <h3 className="font-semibold text-[#2C3E30] text-lg mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-[#C4A962]" strokeWidth={1.5} /> Alertes
                </h3>
                <div className="space-y-2">
                  {overview.alerts?.length > 0 ? overview.alerts.map((a, i) => (
                    <div key={i} data-testid={`alert-${i}`} className={`flex items-start gap-3 p-3 rounded-lg text-sm ${
                      a.severity === 'urgent' ? 'bg-red-50 text-[#993333]' : a.severity === 'warning' ? 'bg-amber-50 text-amber-800' : 'bg-blue-50 text-blue-700'
                    }`}>
                      <div className="mt-0.5">
                        {a.severity === 'urgent' ? <AlertTriangle className="w-4 h-4" /> : a.severity === 'warning' ? <Clock className="w-4 h-4" /> : <ClipboardList className="w-4 h-4" />}
                      </div>
                      <span>{a.message}</span>
                      {a.type === 'payment_due' && a.reservation_id && (
                        <button onClick={() => { setTab('paiements'); }} className="ml-auto text-xs font-medium underline underline-offset-2 flex-shrink-0">Voir</button>
                      )}
                    </div>
                  )) : (
                    <p className="text-sm text-[#8A9A5B]">Aucune alerte active.</p>
                  )}
                </div>

                {/* Arrivals / Departures detail */}
                {(overview.arrivals_today?.length > 0 || overview.departures_today?.length > 0) && (
                  <div className="mt-6 pt-4 border-t border-[#E5E5E0]">
                    {overview.arrivals_today?.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-[#2C3E30] mb-2">Arrivees aujourd'hui</h4>
                        {overview.arrivals_today.map(r => (
                          <div key={r.id} className="flex items-center justify-between p-2 rounded-lg bg-emerald-50 text-sm mb-1.5">
                            <div className="flex items-center gap-2">
                              <PlaneLanding className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
                              <span className="font-medium">{r.name}</span>
                              <span className="text-[#595959]">{FORMULES[r.accommodation_type] || r.accommodation_type}</span>
                            </div>
                            <a href={`mailto:${r.email}`} className="text-[#8A9A5B] hover:text-[#7a8a4d]" data-testid={`contact-${r.id}`}>
                              <Mail className="w-4 h-4" strokeWidth={1.5} />
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                    {overview.departures_today?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-[#2C3E30] mb-2">Departs aujourd'hui</h4>
                        {overview.departures_today.map(r => (
                          <div key={r.id} className="flex items-center justify-between p-2 rounded-lg bg-blue-50 text-sm mb-1.5">
                            <div className="flex items-center gap-2">
                              <Plane className="w-4 h-4 text-blue-600" strokeWidth={1.5} />
                              <span className="font-medium">{r.name}</span>
                              <span className="text-[#595959]">{FORMULES[r.accommodation_type] || r.accommodation_type}</span>
                            </div>
                            <a href={`mailto:${r.email}`} className="text-[#8A9A5B] hover:text-[#7a8a4d]">
                              <Mail className="w-4 h-4" strokeWidth={1.5} />
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right sidebar - shortcuts + recent */}
              <div className="space-y-6">
                {/* Quick shortcuts */}
                <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
                  <h3 className="font-semibold text-[#2C3E30] text-sm mb-3">Raccourcis rapides</h3>
                  <div className="space-y-2">
                    <Button
                      data-testid="shortcut-new-reservation"
                      onClick={() => setTab('reservations')}
                      className="w-full justify-start rounded-lg bg-[#8A9A5B] text-white hover:bg-[#7a8a4d] text-sm h-10"
                      size="sm"
                    >
                      <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Reservations
                    </Button>
                    <Button
                      data-testid="shortcut-pricing"
                      onClick={() => navigate('/admin/tarifs')}
                      variant="outline"
                      className="w-full justify-start rounded-lg border-[#E5E5E0] text-[#595959] text-sm h-10"
                      size="sm"
                    >
                      <DollarSign className="w-4 h-4 mr-2" strokeWidth={1.5} /> Modifier tarifs
                    </Button>
                    <Button
                      data-testid="shortcut-medias"
                      onClick={() => navigate('/admin/medias')}
                      variant="outline"
                      className="w-full justify-start rounded-lg border-[#E5E5E0] text-[#595959] text-sm h-10"
                      size="sm"
                    >
                      <ImageIcon className="w-4 h-4 mr-2" strokeWidth={1.5} /> Gerer les medias
                    </Button>
                    <Button
                      data-testid="shortcut-avis"
                      onClick={() => navigate('/admin/avis')}
                      variant="outline"
                      className="w-full justify-start rounded-lg border-[#E5E5E0] text-[#595959] text-sm h-10"
                      size="sm"
                    >
                      <Star className="w-4 h-4 mr-2" strokeWidth={1.5} /> Gerer les avis
                    </Button>
                    <Button
                      data-testid="shortcut-settings"
                      onClick={() => navigate('/admin/parametres')}
                      variant="outline"
                      className="w-full justify-start rounded-lg border-[#E5E5E0] text-[#595959] text-sm h-10"
                      size="sm"
                    >
                      <Settings className="w-4 h-4 mr-2" strokeWidth={1.5} /> Parametres
                    </Button>
                  </div>
                </div>

                {/* Recent reservations */}
                <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
                  <h3 className="font-semibold text-[#2C3E30] text-sm mb-3">Dernieres reservations</h3>
                  <div className="space-y-2">
                    {overview.recent_reservations?.map(r => (
                      <div key={r.id} className="flex items-center justify-between p-2 rounded-lg bg-[#F5F5F0] text-xs">
                        <div>
                          <span className="font-medium text-[#2C3E30]">{r.name}</span>
                          <span className="text-[#595959] ml-1.5">{r.arrival_date}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-[#2C3E30]">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(r.amount)}</span>
                          <a href={`mailto:${r.email}`} title="Contacter" className="text-[#8A9A5B] hover:text-[#7a8a4d]">
                            <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Reservations tab */}
        {tab === 'reservations' && (
          <div className="bg-white rounded-xl shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] overflow-hidden">
            {/* Filter bar + Export */}
            <div className="px-4 py-3 border-b border-[#E5E5E0] flex items-center gap-2 flex-wrap" data-testid="reservations-filter-bar">
              <Filter className="w-4 h-4 text-[#595959] shrink-0" strokeWidth={1.5} />
              <Select value={filterSource} onValueChange={setFilterSource}>
                <SelectTrigger className="w-[130px] h-8 text-xs border-[#E5E5E0]" data-testid="filter-source">
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes sources</SelectItem>
                  {[...new Set(reservations.map(r => r.source || 'Site direct'))].sort().map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[130px] h-8 text-xs border-[#E5E5E0]" data-testid="filter-status">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous statuts</SelectItem>
                  {Object.entries(STATUS_MAP).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterPeriod} onValueChange={setFilterPeriod}>
                <SelectTrigger className="w-[140px] h-8 text-xs border-[#E5E5E0]" data-testid="filter-period">
                  <SelectValue placeholder="Periode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes periodes</SelectItem>
                  <SelectItem value="future">A venir</SelectItem>
                  <SelectItem value="month">Ce mois</SelectItem>
                  <SelectItem value="3months">3 prochains mois</SelectItem>
                  <SelectItem value="past">Passees</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-[#888] ml-auto whitespace-nowrap">
                {(() => {
                  const filtered = displayReservations.filter(r => {
                    if (filterSource !== 'all' && (r.source || 'Site direct') !== filterSource) return false;
                    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
                    if (filterPeriod !== 'all') {
                      const today = new Date().toISOString().slice(0, 10);
                      const d = new Date();
                      if (filterPeriod === 'future' && r.arrival_date < today) return false;
                      if (filterPeriod === 'past' && r.departure_date > today) return false;
                      if (filterPeriod === 'month') {
                        const monthStart = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
                        const nextMonth = new Date(d.getFullYear(), d.getMonth()+1, 1);
                        const monthEnd = nextMonth.toISOString().slice(0, 10);
                        if (r.arrival_date >= monthEnd || r.departure_date < monthStart) return false;
                      }
                      if (filterPeriod === '3months') {
                        const end3 = new Date(d.getFullYear(), d.getMonth()+3, d.getDate()).toISOString().slice(0,10);
                        if (r.arrival_date < today || r.arrival_date > end3) return false;
                      }
                    }
                    return true;
                  });
                  return `${filtered.length} res.`;
                })()}
              </span>
              <Button size="sm" className="h-8 text-xs bg-[#2C3E30] hover:bg-[#3a5240] shrink-0" onClick={() => setShowNewResa(true)} data-testid="new-reservation-btn">
                <Plus className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Nouvelle reservation
              </Button>
              <a href={`${BACKEND_URL}/api/admin/reservations/export-csv?token=${localStorage.getItem('admin_token')}`} download>
                <Button variant="outline" size="sm" className="h-8 text-xs border-[#E5E5E0] shrink-0" data-testid="export-csv-btn">
                  <Download className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> CSV
                </Button>
              </a>
            </div>

            {/* Tourist tax regularization banner */}
            {taxSummary && taxSummary.count > 0 && (
              <div className={`px-4 py-3 border-b border-[#E5E5E0] text-xs flex items-center gap-2 flex-wrap ${taxSummary.missing_adults > 0 ? 'bg-amber-50 text-amber-900' : 'bg-emerald-50 text-emerald-800'}`} data-testid="tax-regularization-banner">
                <span className="font-semibold">Taxe de sejour — reservations externes :</span>
                <span>{taxSummary.count - taxSummary.missing_adults}/{taxSummary.count} renseignees</span>
                <span>•</span>
                <span data-testid="tax-regularization-total">Total a regulariser : <strong>{new Intl.NumberFormat('fr-FR',{style:'currency',currency:'EUR'}).format(taxSummary.total_tourist_tax || 0)}</strong></span>
                {taxSummary.missing_adults > 0 && (
                  <span className="opacity-80">— cliquez sur l&apos;icone occupants d&apos;une ligne &laquo; Externe &raquo; pour renseigner adultes/enfants</span>
                )}
              </div>
            )}

            {/* Table */}
            {loading ? (
              <div className="p-12 text-center text-[#595959]">Chargement...</div>
            ) : reservations.length === 0 ? (
              <div className="p-12 text-center text-[#595959]">Aucune reservation pour le moment.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F5F0]">
                      <TableHead className="text-[#2C3E30] font-medium text-xs whitespace-nowrap">Source</TableHead>
                      <TableHead className="text-[#2C3E30] font-medium text-xs">Client</TableHead>
                      <TableHead className="text-[#2C3E30] font-medium text-xs">Formule</TableHead>
                      <TableHead className="text-[#2C3E30] font-medium text-xs whitespace-nowrap">Dates</TableHead>
                      <TableHead className="text-[#2C3E30] font-medium text-xs">Statut</TableHead>
                      <TableHead className="text-[#2C3E30] font-medium text-xs text-right whitespace-nowrap">Montant</TableHead>
                      <TableHead className="text-[#2C3E30] font-medium text-xs">Paiement</TableHead>
                      <TableHead className="text-[#2C3E30] font-medium text-xs text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayReservations.filter(r => {
                      if (filterSource !== 'all' && (r.source || 'Site direct') !== filterSource) return false;
                      if (filterStatus !== 'all' && r.status !== filterStatus) return false;
                      if (filterPeriod !== 'all') {
                        const today = new Date().toISOString().slice(0, 10);
                        const d = new Date();
                        if (filterPeriod === 'future' && r.arrival_date < today) return false;
                        if (filterPeriod === 'past' && r.departure_date > today) return false;
                        if (filterPeriod === 'month') {
                          const monthStart = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
                          const nextMonth = new Date(d.getFullYear(), d.getMonth()+1, 1);
                          const monthEnd = nextMonth.toISOString().slice(0, 10);
                          if (r.arrival_date >= monthEnd || r.departure_date < monthStart) return false;
                        }
                        if (filterPeriod === '3months') {
                          const end3 = new Date(d.getFullYear(), d.getMonth()+3, d.getDate()).toISOString().slice(0,10);
                          if (r.arrival_date < today || r.arrival_date > end3) return false;
                        }
                      }
                      return true;
                    }).map(r => {
                      const isExternal = r.entry_type === 'ical_import';
                      return (
                      <TableRow key={r.id} data-testid={`reservation-row-${r.id}`} className={`hover:bg-[#FAFAF9] ${isExternal ? 'bg-blue-50/30' : ''}`}>
                        <TableCell className="py-2 px-3">
                          <Badge className={`text-[10px] border whitespace-nowrap ${SOURCE_COLORS[r.source] || SOURCE_COLORS['Externe']}`} data-testid={`source-badge-${r.id}`}>
                            {r.source || 'Site direct'}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-2 px-3">
                          {isExternal ? (
                            <p className="text-xs text-[#888] italic whitespace-nowrap">Reserv. externe</p>
                          ) : (
                            <div className="min-w-[100px]">
                              <p className="font-medium text-xs text-[#1C1C1E] cursor-pointer hover:text-[#C4A962] transition-colors truncate max-w-[140px]" onClick={() => setSelectedReservation(r)} data-testid={`reservation-name-${r.id}`}>{r.name}</p>
                              <p className="text-[10px] text-[#595959] truncate max-w-[140px]">{r.email}</p>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="py-2 px-3">
                          <span className="text-xs whitespace-nowrap">{r.accommodation_label || FORMULES[r.accommodation_type] || r.accommodation_type}</span>
                        </TableCell>
                        <TableCell className="py-2 px-3">
                          <span className="text-xs whitespace-nowrap">{r.arrival_date}<br/>{r.departure_date}</span>
                        </TableCell>
                        <TableCell className="py-2 px-3">
                          <Badge className={`${STATUS_MAP[r.status]?.color || 'bg-gray-100'} border text-[10px] whitespace-nowrap`}>
                            {STATUS_MAP[r.status]?.label || r.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right py-2 px-3">
                          {isExternal ? (
                            <span className="text-xs text-[#888]">—</span>
                          ) : (
                            <span className="text-xs font-semibold whitespace-nowrap">{new Intl.NumberFormat('fr-FR',{style:'currency',currency:'EUR',minimumFractionDigits:0}).format(r.amount || 0)}</span>
                          )}
                        </TableCell>
                        <TableCell className="py-2 px-3">
                          {isExternal ? (
                            <span className="text-xs text-[#888]">—</span>
                          ) : (
                            <Badge className={`text-[10px] border whitespace-nowrap ${
                              r.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                              r.payment_status === 'deposit_paid' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                              r.payment_status === 'refunded' ? 'bg-purple-100 text-purple-700 border-purple-200' :
                              r.payment_status === 'deposit_only' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                              r.payment_status === 'refund_pending' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                              r.payment_status === 'refund_rejected' ? 'bg-red-100 text-red-700 border-red-200' :
                              r.payment_status === 'failed' ? 'bg-red-100 text-red-700 border-red-200' :
                              'bg-red-100 text-red-700 border-red-200'
                            }`}>
                              {r.payment_status === 'paid' ? 'Paye' : r.payment_status === 'deposit_paid' ? 'Acompte' : r.payment_status === 'refunded' ? 'Rembourse' : r.payment_status === 'deposit_only' ? 'Acompte seul' : r.payment_status === 'refund_pending' ? 'Remb. att.' : r.payment_status === 'refund_rejected' ? 'Remb. refuse' : r.payment_status === 'failed' ? 'Echec' : 'Non paye'}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right py-2 px-3">
                          {isExternal ? (
                            <span className="text-xs text-[#888]">—</span>
                          ) : (
                            <div className="flex items-center justify-end gap-0.5">
                              {r.source === 'Externe' && (
                                <Button
                                  data-testid={`occupants-btn-${r.id}`}
                                  size="sm" variant="ghost"
                                  onClick={() => {
                                    setOccAdults(r.adults ? String(r.adults) : '');
                                    setOccChildren(String(r.children || 0));
                                    setOccupantsModal(r);
                                  }}
                                  className={`h-7 w-7 p-0 ${r.adults ? 'text-[#8A9A5B] hover:text-[#7a8a4d]' : 'text-amber-500 hover:text-amber-600 hover:bg-amber-50'}`}
                                  title={r.adults ? `${r.adults} adulte(s), ${r.children || 0} enfant(s) — taxe ${r.tourist_tax || 0} EUR` : 'Renseigner adultes/enfants (taxe de sejour)'}
                                >
                                  <Users className="w-3.5 h-3.5" strokeWidth={2} />
                                </Button>
                              )}
                              {r.payment_status === 'deposit_paid' && (
                                <Button
                                  data-testid={`balance-link-btn-${r.id}`}
                                  size="sm" variant="ghost"
                                  onClick={() => {
                                    const url = `${window.location.origin}/paiement/solde/${r.id}?email=${encodeURIComponent(r.email)}`;
                                    navigator.clipboard.writeText(url);
                                    setCopiedLink(r.id);
                                    setTimeout(() => setCopiedLink(null), 2000);
                                  }}
                                  className="text-[#C4A962] hover:text-[#b5945a] hover:bg-[#C4A962]/10 h-7 w-7 p-0"
                                  title="Copier lien paiement solde"
                                >
                                  {copiedLink === r.id ? <Check className="w-3.5 h-3.5" strokeWidth={2} /> : <Link2 className="w-3.5 h-3.5" strokeWidth={2} />}
                                </Button>
                              )}
                              {(r.payment_status === 'deposit_paid' || r.payment_status === 'paid') && r.status !== 'cancelled' && (
                                <Button
                                  data-testid={`refund-btn-${r.id}`}
                                  size="sm" variant="ghost"
                                  onClick={() => { setRefundId(r.id); setRefundType('deposit'); setRefundResult(null); }}
                                  className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 h-7 w-7 p-0"
                                  title="Rembourser"
                                >
                                  <RotateCcw className="w-3.5 h-3.5" strokeWidth={2} />
                                </Button>
                              )}
                              {r.status !== 'confirmed' && (
                                <Button
                                  data-testid={`confirm-btn-${r.id}`}
                                  size="sm" variant="ghost"
                                  onClick={() => updateStatus(r.id, 'confirmed')}
                                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 h-7 w-7 p-0"
                                  title="Confirmer"
                                >
                                  <Check className="w-3.5 h-3.5" strokeWidth={2} />
                                </Button>
                              )}
                              {r.status !== 'cancelled' && (
                                <Button
                                  data-testid={`cancel-btn-${r.id}`}
                                  size="sm" variant="ghost"
                                  onClick={() => cancelWithUndo(r.id)}
                                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-7 w-7 p-0"
                                  title="Annuler"
                                >
                                  <XIcon className="w-3.5 h-3.5" strokeWidth={2} />
                                </Button>
                              )}
                              <Button
                                data-testid={`delete-btn-${r.id}`}
                                size="sm" variant="ghost"
                                onClick={() => setDeleteId(r.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                                title="Supprimer"
                              >
                                <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}


        {/* Paiements tab */}
        {tab === 'paiements' && (
          <div className="space-y-6">
            {/* Auto-cancel section */}
            <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-[#2C3E30] text-lg">Soldes en retard</h3>
                  <p className="text-sm text-[#595959]">Reservations dont le delai de paiement du solde est depasse ou approche.</p>
                </div>
                <Button
                  data-testid="auto-cancel-btn"
                  onClick={handleAutoCancel}
                  disabled={autoCancelLoading}
                  className="rounded-full bg-[#993333] text-white hover:bg-[#7a2828]"
                  size="sm"
                >
                  {autoCancelLoading ? 'Verification...' : 'Annuler les impayes'}
                </Button>
              </div>

              {autoCancelResult && (
                <div className="mb-4 p-3 rounded-lg bg-amber-50 text-amber-800 text-sm">
                  {autoCancelResult.message}
                  {autoCancelResult.cancelled > 0 && (
                    <ul className="mt-2 space-y-1">
                      {autoCancelResult.details?.map(d => (
                        <li key={d.id}>{d.name} - Solde du: {d.balance_due_date} - Acompte {d.deposit_refundable ? 'remboursable' : 'acquis'}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Overdue reservations */}
              {overdueReport?.overdue?.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-[#993333] text-sm mb-2">En retard ({overdueReport.overdue.length})</h4>
                  <div className="space-y-2">
                    {overdueReport.overdue.map(r => (
                      <div key={r.id} className="flex items-center justify-between p-3 rounded-lg bg-red-50 text-sm">
                        <div>
                          <span className="font-medium">{r.name}</span>
                          <span className="text-[#595959] ml-2">{FORMULES[r.accommodation_type] || r.accommodation_type}</span>
                          <span className="text-[#595959] ml-2">{r.arrival_date}</span>
                          {r.invoice_status === 'open' && <Badge className="ml-2 text-[10px] bg-blue-50 text-blue-700 border-blue-200 border">Prelevement auto planifie</Badge>}
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-[#993333]">{new Intl.NumberFormat('fr-FR',{style:'currency',currency:'EUR',minimumFractionDigits:0}).format(r.balance_due)} du</span>
                          <span className="text-xs text-[#993333] ml-1">(echeance: {r.balance_due_date})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upcoming deadlines */}
              {overdueReport?.upcoming?.length > 0 && (
                <div>
                  <h4 className="font-medium text-amber-700 text-sm mb-2">Echeances proches ({overdueReport.upcoming.length})</h4>
                  <div className="space-y-2">
                    {overdueReport.upcoming.map(r => (
                      <div key={r.id} className="flex items-center justify-between p-3 rounded-lg bg-amber-50 text-sm">
                        <div>
                          <span className="font-medium">{r.name}</span>
                          <span className="text-[#595959] ml-2">{FORMULES[r.accommodation_type] || r.accommodation_type}</span>
                          <span className="text-[#595959] ml-2">{r.arrival_date}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-amber-700">{new Intl.NumberFormat('fr-FR',{style:'currency',currency:'EUR',minimumFractionDigits:0}).format(r.balance_due)}</span>
                          <span className="text-xs text-amber-600 ml-1">({r.days_left}j restants)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {overdueReport && !overdueReport.overdue?.length && !overdueReport.upcoming?.length && (
                <p className="text-sm text-[#8A9A5B]">Aucun solde en retard ou a echeance proche.</p>
              )}
            </div>

            {/* Caution management */}
            <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#2C3E30] text-lg">Gestion des cautions</h3>
                <Button
                  data-testid="cautions-full-page-link"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/admin/cautions')}
                  className="text-[#2C3E30] border-[#E5E5E0] hover:bg-[#F5F5F0] text-xs"
                >
                  <Shield className="w-3 h-3 mr-1" /> Gestion complete
                </Button>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F5F0]">
                      <TableHead className="text-[#2C3E30] font-medium">Client</TableHead>
                      <TableHead className="text-[#2C3E30] font-medium">Formule</TableHead>
                      <TableHead className="text-[#2C3E30] font-medium">Dates</TableHead>
                      <TableHead className="text-[#2C3E30] font-medium text-right">Caution</TableHead>
                      <TableHead className="text-[#2C3E30] font-medium">Statut caution</TableHead>
                      <TableHead className="text-[#2C3E30] font-medium text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reservations.filter(r => r.caution_amount > 0 && r.status === 'confirmed').map(r => (
                      <TableRow key={r.id}>
                        <TableCell className="text-sm font-medium">{r.name}</TableCell>
                        <TableCell className="text-sm">{FORMULES[r.accommodation_type] || r.accommodation_type}</TableCell>
                        <TableCell className="text-sm">{r.arrival_date} → {r.departure_date}</TableCell>
                        <TableCell className="text-right text-sm font-semibold">{new Intl.NumberFormat('fr-FR',{style:'currency',currency:'EUR',minimumFractionDigits:0}).format(r.caution_amount)}</TableCell>
                        <TableCell>
                          <Badge className={`text-xs border ${
                            r.caution_status === 'authorized' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                            r.caution_status === 'released' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                            r.caution_status === 'captured' || r.caution_status === 'partially_captured' ? 'bg-red-100 text-red-700 border-red-200' :
                            'bg-gray-100 text-gray-700 border-gray-200'
                          }`}>
                            {r.caution_status === 'authorized' ? 'Pre-autorisee' :
                             r.caution_status === 'released' ? 'Liberee' :
                             r.caution_status === 'captured' ? 'Capturee' :
                             r.caution_status === 'partially_captured' ? 'Capture partielle' : 'En attente'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {r.caution_status === 'authorized' && (
                            <Button
                              data-testid={`release-caution-${r.id}`}
                              size="sm" variant="ghost"
                              onClick={() => handleReleaseCaution(r.id)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-xs"
                            >
                              Liberer
                            </Button>
                          )}
                          {r.caution_status === 'pending' && (
                            <Button
                              data-testid={`authorize-caution-dashboard-${r.id}`}
                              size="sm" variant="ghost"
                              onClick={() => handleAuthorizeCaution(r.id)}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 text-xs"
                            >
                              Autoriser
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Payment conditions summary */}
            <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
              <h3 className="font-semibold text-[#2C3E30] text-lg mb-4">Conditions de paiement</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-[#F5F5F0]">
                  <h4 className="font-medium text-sm text-[#2C3E30] mb-2">Appartements</h4>
                  <p className="text-xs text-[#595959]">Acompte 30% remboursable | Solde 30j avant | Caution 500 EUR</p>
                </div>
                <div className="p-4 rounded-lg bg-[#F5F5F0]">
                  <h4 className="font-medium text-sm text-[#2C3E30] mb-2">Gite seul</h4>
                  <p className="text-xs text-[#595959]">Acompte 30% remboursable | Solde 30j avant | Caution 1 000 EUR</p>
                </div>
                <div className="p-4 rounded-lg bg-[#F5F5F0]">
                  <h4 className="font-medium text-sm text-[#2C3E30] mb-2">Gite + Salle (hors mariage)</h4>
                  <p className="text-xs text-[#595959]">Acompte 30% non remboursable | Solde 2 mois avant | Caution 2 500 EUR</p>
                </div>
                <div className="p-4 rounded-lg bg-[#F5F5F0]">
                  <h4 className="font-medium text-sm text-[#2C3E30] mb-2">Mariage</h4>
                  <p className="text-xs text-[#595959]">Acompte 30% non remboursable | Solde 6 mois avant | Caution 2 500 EUR</p>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Emails tab */}
        {tab === 'emails' && (
          <div className="space-y-6">
            {/* Controls */}
            <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-[#2C3E30] text-lg">Emails de suivi automatiques</h3>
                  <p className="text-sm text-[#595959]">Emails programmes envoyes automatiquement aux clients selon les dates de sejour.</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid="email-toggle-pause"
                    onClick={toggleEmailPause}
                    variant="outline"
                    size="sm"
                    className={`rounded-full text-sm ${emailStats?.paused ? 'border-emerald-300 text-emerald-700' : 'border-amber-300 text-amber-700'}`}
                  >
                    {emailStats?.paused ? 'Reactiver' : 'Mettre en pause'}
                  </Button>
                  <Button
                    data-testid="email-trigger-btn"
                    onClick={triggerEmails}
                    size="sm"
                    className="rounded-full bg-[#8A9A5B] text-white hover:bg-[#7a8a4d] text-sm"
                  >
                    <Mail className="w-4 h-4 mr-1" /> Executer maintenant
                  </Button>
                </div>
              </div>
              {emailStats?.paused && (
                <div className="p-3 rounded-lg bg-amber-50 text-amber-800 text-sm mb-4">
                  Les emails automatiques sont actuellement en pause.
                </div>
              )}
              {triggerResult && (
                <div className="p-3 rounded-lg bg-emerald-50 text-emerald-700 text-sm mb-4">
                  {triggerResult.message}
                </div>
              )}
            </div>

            {/* Email types with toggle */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {emailStats && Object.entries(emailStats.by_type || {}).map(([etype, info]) => {
                const isDisabled = (emailStats.disabled_types || []).includes(etype);
                const econfig = {
                  j_minus_30: { icon: '30', color: 'bg-blue-50 text-blue-700 border-blue-200', desc: 'Infos pratiques, suggestions d\'activites' },
                  j_minus_7: { icon: '7', color: 'bg-amber-50 text-amber-700 border-amber-200', desc: 'Rappel, demande d\'heure d\'arrivee' },
                  j_plus_1: { icon: '+1', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', desc: 'Lien avis Google, invitation a revenir' },
                  j_plus_90: { icon: '90', color: 'bg-purple-50 text-purple-700 border-purple-200', desc: 'Offre fidelite, invitation a reserver' },
                }[etype] || { icon: '?', color: 'bg-gray-50 text-gray-700 border-gray-200', desc: '' };
                return (
                  <div key={etype} data-testid={`email-type-${etype}`} className={`bg-white rounded-xl p-5 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] border ${isDisabled ? 'opacity-50' : ''}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border ${econfig.color}`}>
                          J{econfig.icon.startsWith('+') ? '' : '-'}{econfig.icon}
                        </div>
                        <div>
                          <p className="font-medium text-sm text-[#2C3E30]">{info.label}</p>
                          <p className="text-xs text-[#595959]">{econfig.desc}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleEmailType(etype)}
                        data-testid={`email-toggle-${etype}`}
                        className={`w-10 h-5 rounded-full relative transition-colors ${isDisabled ? 'bg-[#E5E5E0]' : 'bg-[#8A9A5B]'}`}
                      >
                        <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-all shadow-sm ${isDisabled ? 'left-0.5' : 'left-[22px]'}`} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#595959]">{info.count} envoye(s)</span>
                      <div className="flex items-center gap-3">
                        {templates[etype]?.is_custom && (
                          <span className="text-[#8A9A5B] text-[10px] font-medium">Personnalise</span>
                        )}
                        <button onClick={() => openEditTemplate(etype)} className="text-[#595959] hover:text-[#2C3E30] font-medium" data-testid={`email-edit-${etype}`}>
                          Modifier
                        </button>
                        <button onClick={() => previewEmail(etype)} className="text-[#C4A962] hover:text-[#b5945a] font-medium" data-testid={`email-preview-${etype}`}>
                          Previsualiser
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Stats */}
            {emailStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 text-center shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
                  <p className="text-2xl font-semibold text-[#2C3E30]">{emailStats.total}</p>
                  <p className="text-xs text-[#595959]">Total emails</p>
                </div>
                <div className="bg-white rounded-xl p-4 text-center shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
                  <p className="text-2xl font-semibold text-emerald-600">{emailStats.sent}</p>
                  <p className="text-xs text-[#595959]">Envoyes</p>
                </div>
                <div className="bg-white rounded-xl p-4 text-center shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
                  <p className="text-2xl font-semibold text-amber-600">{emailStats.simulated}</p>
                  <p className="text-xs text-[#595959]">Simules</p>
                </div>
                <div className="bg-white rounded-xl p-4 text-center shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
                  <p className="text-2xl font-semibold text-red-500">{emailStats.errors}</p>
                  <p className="text-xs text-[#595959]">Erreurs</p>
                </div>
              </div>
            )}

            {/* Recent emails log */}
            <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]">
              <h3 className="font-semibold text-[#2C3E30] text-lg mb-4">Historique des emails</h3>
              {emails.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F5F5F0]">
                        <TableHead className="text-[#2C3E30] font-medium">Date</TableHead>
                        <TableHead className="text-[#2C3E30] font-medium">Type</TableHead>
                        <TableHead className="text-[#2C3E30] font-medium">Destinataire</TableHead>
                        <TableHead className="text-[#2C3E30] font-medium">Objet</TableHead>
                        <TableHead className="text-[#2C3E30] font-medium">Statut</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {emails.slice(0, 50).map((e, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs text-[#595959]">{new Date(e.created_at).toLocaleDateString('fr-FR')} {new Date(e.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</TableCell>
                          <TableCell><Badge className="text-xs bg-[#F5F5F0] text-[#595959] border-[#E5E5E0] border">{emailStats?.email_types?.[e.email_type] || e.email_type}</Badge></TableCell>
                          <TableCell className="text-sm">
                            <span className="font-medium">{e.recipient_name}</span>
                            <span className="text-[#595959] ml-1 text-xs">{e.recipient}</span>
                          </TableCell>
                          <TableCell className="text-sm text-[#595959]">{e.subject}</TableCell>
                          <TableCell>
                            <Badge className={`text-xs border ${
                              e.status === 'sent' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                              e.status === 'simulated' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                              'bg-red-100 text-red-700 border-red-200'
                            }`}>
                              {e.status === 'sent' ? 'Envoye' : e.status === 'simulated' ? 'Simule' : 'Erreur'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-[#595959]">Aucun email envoye pour le moment. Les emails seront generes automatiquement selon les dates de sejour.</p>
              )}
            </div>
          </div>
        )}



        {/* Taches tab */}
        {tab === 'taches' && (
          <TasksOverview token={token} />
        )}

        {/* Disponibilites tab */}
        {tab === 'disponibilites' && (() => {
          // Build a lookup: reservation_id → reservation (for client name)
          const resaById = {};
          for (const r of reservations) if (r.id) resaById[r.id] = r;

          // Classify each block by origin
          const originOf = (b) => {
            if (b.reason === 'amenitiz_import') return 'Amenitiz';
            if (b.reason === 'ical_sync') return 'iCal';
            return 'Manuel';
          };
          // Display label (client name OR reason)
          const labelOf = (b) => {
            if (b.reason === 'amenitiz_import' || b.reason === 'ical_sync') {
              const r = b.reservation_id && resaById[b.reservation_id];
              if (r && r.name) return r.name;
              if (b.reason === 'ical_sync') return `Reservation ${b.ical_platform || 'externe'}`;
              return 'Reservation Amenitiz';
            }
            return b.reason || 'Blocage manuel';
          };
          // Keep only legitimate, user-facing sources:
          //  - Amenitiz primary entries (acc_type == primary acc)
          //  - iCal primary entries (acc_type == ical_source) to avoid duplicates across cross-blocking
          //  - True manual blocks (Travaux, etc.) i.e. anything that is NOT reservation/cross_block/auxiliary
          // We hide:
          //  - reason == 'cross_block'      (echoes of other blocks)
          //  - reason == 'reservation'      (already listed in the Reservations tab)
          //  - auxiliary iCal/Amenitiz rows (acc_type != ical_source / primary acc)
          const SYSTEM_HIDDEN = new Set(['reservation', 'cross_block']);
          const visibleBlocks = blockedDates.filter(b => {
            if (SYSTEM_HIDDEN.has(b.reason)) return false;
            if (b.reason === 'ical_sync' && b.ical_source && b.accommodation_type !== b.ical_source) return false;
            return true;
          });
          // Sort ASC by start_date (closest in time first), then filter
          const filtered = [...visibleBlocks]
            .sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''))
            .filter(b => availFilterFormula === 'all' || b.accommodation_type === availFilterFormula)
            .filter(b => availFilterOrigin === 'all' || originOf(b) === availFilterOrigin);

          const originBadgeCls = {
            Amenitiz: 'bg-emerald-50 text-emerald-700 border-emerald-200',
            iCal: 'bg-blue-50 text-blue-700 border-blue-200',
            Manuel: 'bg-amber-50 text-amber-700 border-amber-200',
          };

          return (
            <div data-testid="disponibilites-section">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={availFilterFormula} onValueChange={setAvailFilterFormula}>
                    <SelectTrigger data-testid="avail-filter-formula" className="w-[200px] rounded-full h-9 text-sm"><SelectValue placeholder="Formule" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes formules</SelectItem>
                      <SelectItem value="appartement-1">Apt Terrasse</SelectItem>
                      <SelectItem value="appartement-2">Apt Panorama</SelectItem>
                      <SelectItem value="gite-seul">Gite seul</SelectItem>
                      <SelectItem value="gite-salle">Gite + Salle</SelectItem>
                      <SelectItem value="salle-seule">Salle seule</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={availFilterOrigin} onValueChange={setAvailFilterOrigin}>
                    <SelectTrigger data-testid="avail-filter-origin" className="w-[180px] rounded-full h-9 text-sm"><SelectValue placeholder="Origine" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes origines</SelectItem>
                      <SelectItem value="Amenitiz">Amenitiz</SelectItem>
                      <SelectItem value="iCal">iCal</SelectItem>
                      <SelectItem value="Manuel">Manuel</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-[#595959] ml-2" data-testid="avail-count">{filtered.length} entree(s)</span>
                </div>
                <Button data-testid="admin-block-dates-btn" onClick={() => setBlockDialog(true)} className="rounded-full bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040]">
                  <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> Bloquer des dates
                </Button>
              </div>

              <div className="bg-white rounded-xl shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] overflow-hidden">
                {filtered.length === 0 ? (
                  <div className="p-12 text-center text-[#595959]">Aucune date bloquee ne correspond aux filtres.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#F5F5F0]">
                          <TableHead className="text-[#2C3E30] font-medium">Formule</TableHead>
                          <TableHead className="text-[#2C3E30] font-medium">Du</TableHead>
                          <TableHead className="text-[#2C3E30] font-medium">Au</TableHead>
                          <TableHead className="text-[#2C3E30] font-medium">Client / Raison</TableHead>
                          <TableHead className="text-[#2C3E30] font-medium">Origine</TableHead>
                          <TableHead className="text-[#2C3E30] font-medium text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.map(b => {
                          const origin = originOf(b);
                          return (
                            <TableRow key={b.id} data-testid={`blocked-row-${b.id}`}>
                              <TableCell className="text-sm">{FORMULES[b.accommodation_type] || b.accommodation_type}</TableCell>
                              <TableCell className="text-sm">{b.start_date}</TableCell>
                              <TableCell className="text-sm">{b.end_date >= '2099-01-01' ? "Jusqu'a nouvel ordre" : b.end_date}</TableCell>
                              <TableCell className="text-sm font-medium" data-testid={`blocked-label-${b.id}`}>{labelOf(b)}</TableCell>
                              <TableCell>
                                <Badge data-testid={`blocked-origin-${b.id}`} className={`text-xs border ${originBadgeCls[origin]}`}>{origin}</Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <Button data-testid={`unblock-btn-${b.id}`} size="sm" variant="ghost" onClick={() => unblockDate(b.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0">
                                  <Trash2 className="w-4 h-4" strokeWidth={2} />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Reservation detail dialog */}
      <ReservationDetail
        reservation={selectedReservation}
        open={!!selectedReservation}
        onOpenChange={(open) => { if (!open) { setSelectedReservation(null); fetchData(); fetchOverview(); } }}
        token={token}
        onTasksChanged={() => fetchOverview()}
      />

      {/* Block dates dialog */}
      {/* New manual reservation modal */}
      <AdminNewReservation open={showNewResa} onOpenChange={setShowNewResa} api={api} onCreated={fetchData} />

      {/* Occupants edit modal (external reservations - tourist tax) */}
      <Dialog open={!!occupantsModal} onOpenChange={(o) => !o && setOccupantsModal(null)}>
        <DialogContent className="sm:max-w-md" data-testid="occupants-modal">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-xl text-[#2C3E30]">Occupants du sejour</DialogTitle>
            <DialogDescription>
              {occupantsModal && (
                <>
                  {occupantsModal.name} — {occupantsModal.arrival_date} au {occupantsModal.departure_date}.
                  La taxe de sejour sera recalculee automatiquement (les mineurs sont exoneres).
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Adultes (18+) *</label>
              <Input
                type="number" min="1" value={occAdults}
                onChange={(e) => setOccAdults(e.target.value)}
                data-testid="occupants-adults-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Enfants (-18)</label>
              <Input
                type="number" min="0" value={occChildren}
                onChange={(e) => setOccChildren(e.target.value)}
                data-testid="occupants-children-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOccupantsModal(null)} data-testid="occupants-cancel-btn">Annuler</Button>
            <Button
              onClick={saveOccupants}
              disabled={occSaving || !occAdults}
              className="bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040]"
              data-testid="occupants-save-btn"
            >
              {occSaving ? 'Enregistrement...' : 'Enregistrer et recalculer la taxe'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={blockDialog} onOpenChange={setBlockDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-xl text-[#2C3E30]">Bloquer des dates</DialogTitle>
            <DialogDescription>Bloquez manuellement des dates pour une formule.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Formule</label>
              <Select value={blockFormula} onValueChange={setBlockFormula}>
                <SelectTrigger data-testid="block-formula-select" className="rounded-lg h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="appartement-1">Apt Terrasse</SelectItem>
                  <SelectItem value="appartement-2">Apt Panorama</SelectItem>
                  <SelectItem value="gite-seul">Gite seul</SelectItem>
                  <SelectItem value="gite-salle">Gite + Salle</SelectItem>
                  <SelectItem value="salle-seule">Salle seule</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Du</label>
                <Popover open={blockStartOpen} onOpenChange={setBlockStartOpen}>
                  <PopoverTrigger asChild>
                    <Button data-testid="block-start-date" variant="outline" className="w-full justify-start text-left font-normal rounded-lg h-11 text-sm">
                      <CalendarDays className="w-4 h-4 mr-2 text-[#8A9A5B]" strokeWidth={1.5} />
                      {blockStart ? format(blockStart, 'dd/MM/yy') : 'Debut'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={blockStart} onSelect={(d) => { setBlockStart(d); setBlockStartOpen(false); }} disabled={(d) => d < new Date()} defaultMonth={blockStart || undefined} />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Au</label>
                <Popover open={blockEndOpen} onOpenChange={setBlockEndOpen}>
                  <PopoverTrigger asChild>
                    <Button data-testid="block-end-date" variant="outline" disabled={blockNoEnd} className="w-full justify-start text-left font-normal rounded-lg h-11 text-sm disabled:opacity-50">
                      <CalendarDays className="w-4 h-4 mr-2 text-[#8A9A5B]" strokeWidth={1.5} />
                      {blockNoEnd ? 'Indeterminee' : (blockEnd ? format(blockEnd, 'dd/MM/yy') : 'Fin')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={blockEnd} onSelect={(d) => { setBlockEnd(d); setBlockEndOpen(false); }} disabled={(d) => d < (blockStart || new Date())} defaultMonth={blockEnd || blockStart || undefined} />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-[#1C1C1E] cursor-pointer select-none" data-testid="block-no-end-label">
              <input
                type="checkbox"
                data-testid="block-no-end-checkbox"
                checked={blockNoEnd}
                onChange={(e) => { setBlockNoEnd(e.target.checked); if (e.target.checked) setBlockEnd(null); }}
                className="w-4 h-4 accent-[#8A9A5B]"
              />
              Sans date de fin (jusqu'a nouvel ordre &mdash; a lever manuellement)
            </label>
            <div>
              <label className="text-sm font-medium text-[#1C1C1E] mb-1.5 block">Raison (optionnel)</label>
              <Input
                data-testid="block-reason-input"
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="Ex: Travaux, maintenance..."
                className="rounded-lg h-11"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockDialog(false)} className="rounded-full">Annuler</Button>
            <Button data-testid="block-confirm-btn" onClick={blockDates} className="rounded-full bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040]">
              Bloquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#2C3E30]">Confirmer la suppression</DialogTitle>
            <DialogDescription>Cette action est irreversible. La reservation et les dates bloquees associees seront supprimees.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)} className="rounded-full">Annuler</Button>
            <Button
              data-testid="delete-confirm-btn"
              onClick={() => deleteReservation(deleteId)}
              className="rounded-full bg-[#993333] text-white hover:bg-[#7a2828]"
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund dialog */}
      <Dialog open={!!refundId} onOpenChange={() => { setRefundId(null); setRefundResult(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-xl text-[#2C3E30]">Remboursement</DialogTitle>
            <DialogDescription>
              Politique d'annulation : gratuit &gt;14j, 50% entre 7-14j, 0% &lt;7j avant arrivee.
            </DialogDescription>
          </DialogHeader>
          {refundResult ? (
            <div className="py-4 text-center">
              <p className="text-[#2C3E30] font-semibold">{refundResult.message}</p>
              {refundResult.policy && <p className="text-sm text-[#595959] mt-2">Politique : {refundResult.policy}</p>}
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Type de remboursement</label>
                <Select value={refundType} onValueChange={setRefundType}>
                  <SelectTrigger data-testid="refund-type-select" className="rounded-lg h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="deposit">Acompte (selon politique d'annulation)</SelectItem>
                    <SelectItem value="full">Remboursement integral</SelectItem>
                    <SelectItem value="partial">Montant personnalise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRefundId(null); setRefundResult(null); }} className="rounded-full">
              {refundResult ? 'Fermer' : 'Annuler'}
            </Button>
            {!refundResult && (
              <Button
                data-testid="refund-confirm-btn"
                disabled={refundLoading}
                onClick={async () => {
                  setRefundLoading(true);
                  try {
                    const res = await api('post', '/api/admin/payments/refund', { reservation_id: refundId, refund_type: refundType });
                    setRefundResult(res.data);
                    fetchData();
                  } catch (e) {
                    setRefundResult({ message: e.response?.data?.detail || 'Erreur' });
                  } finally { setRefundLoading(false); }
                }}
                className="rounded-full bg-purple-600 text-white hover:bg-purple-700"
              >
                {refundLoading ? 'Traitement...' : 'Rembourser'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email preview dialog */}
      <Dialog open={!!emailPreview} onOpenChange={() => setEmailPreview(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{emailPreview?.label}</DialogTitle>
          </DialogHeader>
          {emailPreview && (
            <div className="space-y-3">
              <p className="text-sm text-[#595959]"><strong>Objet :</strong> {emailPreview.subject}</p>
              <div className="border border-[#E5E5E0] rounded-lg overflow-hidden">
                <div dangerouslySetInnerHTML={{ __html: emailPreview.html }} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmailPreview(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Email Template Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={(o) => { if (!o) setEditingTemplate(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#2C3E30]">Modifier le contenu de l'email</DialogTitle>
            <DialogDescription>
              {EMAIL_TYPES_LABELS[editingTemplate] || editingTemplate}
              {templates[editingTemplate]?.is_custom && <span className="ml-2 text-xs text-[#8A9A5B] font-medium">(personnalise)</span>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-1">Objet de l'email</label>
              <Input
                data-testid="template-subject-input"
                value={templateSubject}
                onChange={(e) => setTemplateSubject(e.target.value)}
                className="border-[#E5E5E0]"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2C3E30] block mb-1">Contenu de l'email</label>
              <textarea
                data-testid="template-body-input"
                value={templateBody}
                onChange={(e) => setTemplateBody(e.target.value)}
                rows={14}
                className="w-full rounded-md border border-[#E5E5E0] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8A9A5B]/30 font-mono leading-relaxed"
              />
            </div>
            <div className="p-3 rounded-lg bg-[#F5F5F0] text-xs text-[#595959]">
              <p className="font-medium text-[#2C3E30] mb-1">Variables disponibles :</p>
              <div className="flex flex-wrap gap-2">
                {['{nom}', '{formule}', '{arrivee}', '{depart}', '{voyageurs}'].map(v => (
                  <code key={v} className="bg-white px-2 py-0.5 rounded border border-[#E5E5E0] text-[#2C3E30]">{v}</code>
                ))}
              </div>
              <p className="mt-2">Les lignes commencant par <code className="bg-white px-1 rounded border">-</code> seront formatees en liste a puces.</p>
            </div>
            {templateSaveMsg && (
              <div className="p-2 rounded-lg bg-emerald-50 text-emerald-700 text-sm text-center">
                {templateSaveMsg}
              </div>
            )}
          </div>
          <DialogFooter className="flex justify-between">
            <Button
              data-testid="template-reset-btn"
              variant="outline"
              onClick={resetTemplate}
              className="border-[#E5E5E0] text-[#595959] hover:bg-[#F5F5F0]"
            >
              Reinitialiser par defaut
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setEditingTemplate(null)}>Annuler</Button>
              <Button
                data-testid="template-save-btn"
                onClick={saveTemplate}
                className="bg-[#8A9A5B] text-white hover:bg-[#7a8a4d]"
              >
                Sauvegarder
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
